import { Box, Typography } from "@mui/material"
import React, { useCallback, useContext, useEffect, useState } from "react"
import { SubmitHandler } from "react-hook-form"
import { Loader } from "components/Loader"
import { MainPageContentContainer } from "components/MainPageContentContainer"
import { useMmsService } from "services/mms/context/MmsServiceContext"
import { MmsServiceInterface } from "interfaces/mms-service"
import { IFormRfidFilter, RfidFilter } from "layouts/rfid/rfid-dashboard/filter.tsx"
import { RfidTable } from "layouts/rfid/rfid-dashboard/rfid-table.tsx"
import {
    ListRfidInfo,
    RfidInfo,
    ExportFilterRfid,
    RfidsTableColumnsType,
} from "types/rfid"

import { StatusWarning } from "components/status"
import { OreveButton } from "components/button/OreveButton.tsx"
import {
    ShoppingBagOutlined,
    AddOutlined,
    SaveAltOutlined,
} from "@mui/icons-material"
import { RfidActivationForm } from "layouts/rfid/rfidActivationForm.tsx"
import { SORTORDER, SortOrder } from "types/sort.ts"
import {
    defaultFilterValuesRfids,
    footerStyle,
    rfidsColumns,
    tableType,
    buttonDefs,
    RfidStatus,
} from "layouts/rfid/utils/const.ts"
import { filterRfids, rfidSort } from "layouts/rfid/utils/functions.ts"
import {
    RfidDisableCard,
    RfidReactivateCard,
} from "layouts/rfid/rfid-dashboard/rfid-popup.tsx"
import { RfidProvider, UseRfidContext } from "layouts/rfid/context/provider.tsx"
import { FeatureToggle } from "App.tsx"
import { ConnexionError } from "components/ConnexionError.tsx"
import { OreveFooter } from "components/OreveFooter.tsx"
import { ChargingRulesPageProvider } from "layouts/charging-rules/context/provider"
import { useChargingRulesContext } from "layouts/charging-rules/context/useChargingRulesContext.ts"
import { EditRuleRfid } from "layouts/rfid/editRuleRfid"
import { SelectableButtonGroup } from "components/button/SelectableButtonGroup.tsx"
import { RfidCard } from "layouts/rfid/rfid-dashboard/rfid-card.tsx"
import { useOrganization } from "context/OrganizationContext"

export const RfidPage = () => {
    const { organizationId } = useOrganization()

    const mmsService: MmsServiceInterface = useMmsService()
    const rfidService = mmsService.getRfidService()
    const [filterObject, setFilterObject] = useState<ExportFilterRfid | null>(null)
    const {
        data: rawRfids,
        success,
        isLoading,
        sendRequest: fetchListRfid,
    } = rfidService.useGetListRfidCards(filterObject)

    useEffect(() => {
        if (filterObject && filterObject?.export === true) {
            fetchListRfid().then(() => {
                setFilterObject({ export: false, filter: filterObject.filter })
            })
        }
    }, [filterObject])

    return (
        <RfidPageProviders
            key={organizationId ?? "no-org"}
            fetchListRfid={fetchListRfid}
        >
            <RfidContent
                rawRfids={rawRfids as ListRfidInfo}
                success={success as boolean}
                isLoading={isLoading}
                fetchListRfid={fetchListRfid}
                setFilterObject={setFilterObject}
            />
        </RfidPageProviders>
    )
}

const RfidPageProviders = ({
    children,
    fetchListRfid,
}: {
    children: React.ReactNode
    fetchListRfid: RequestFunction
}) => {

    return (
        <ChargingRulesPageProvider>
            <RfidProvider fetchListRfid={fetchListRfid}>
                {children}
            </RfidProvider>
        </ChargingRulesPageProvider>
    )
}

const RfidContent = (
    {
        rawRfids,
        success,
        isLoading,
        fetchListRfid,
        setFilterObject
    }: {
        rawRfids: ListRfidInfo
        success: boolean
        isLoading: boolean
        fetchListRfid: () => void
        setFilterObject: React.Dispatch<React.SetStateAction<ExportFilterRfid | null>>
    }
) => {

    const featureContext = useContext(FeatureToggle)
    const [filterValues, setFilterValues] = useState<IFormRfidFilter | null>(
        null
    )
    const [filteredRfids, setFilteredRfids] = useState<ListRfidInfo>([])
    const [noResultFilter, setNoResultFilter] = useState(false)
    const [sortOrder, setSortOrder] = useState<SortOrder>(SORTORDER.ASC)
    const [orderBy, setOrderBy] =
        useState<keyof RfidsTableColumnsType>("driver")
    const [sortedRfids, setSortedRfids] = useState<RfidInfo[]>([])
    const [listRfids, setListRfids] = useState<RfidInfo[]>([])
    const [numberUnactive, setNumberUnactive] = useState<number>(0)
    const { selectedButton } = UseRfidContext()

    useEffect(() => {
        if (rawRfids !== null && success) {
            setListRfids(rawRfids as RfidInfo[])
        }
    }, [rawRfids, success])

    const onFilterSubmit: SubmitHandler<IFormRfidFilter> = useCallback(
        (filterValuesForm) => {
            if (
                JSON.stringify(filterValuesForm) ===
                JSON.stringify(defaultFilterValuesRfids)
            ) {
                setFilterValues(null)
            } else {
                setFilterObject({
                    export: false,
                    filter: {
                        cardHolder: filterValuesForm.cardHolder.trim(),
                        rfidNumber: filterValuesForm.rfidNumber.trim(),
                        status: filterValuesForm.status,
                        type: selectedButton,
                    },
                })
                setFilterValues(trimFilterValuesForm(filterValuesForm))
            }
        },
        []
    )
    const trimFilterValuesForm = (filterValuesForm: IFormRfidFilter) => {
        return {
            cardHolder: filterValuesForm.cardHolder.trim(),
            rfidNumber: filterValuesForm.rfidNumber.trim(),
            status: filterValuesForm.status,
            type: selectedButton,
        }
    }
    function getUnactivateCardText(n: number): string {
        if (n == 1) {
            return "Une carte non activée"
        }
        return numberUnactive + " cartes non activées"
    }

    useEffect(() => {
        if (!filterValues) {
            setFilteredRfids(
                listRfids.filter(
                    (rfid) => rfid.status !== RfidStatus.ToBeActivate
                )
            )
        } else {
            const filterFn = filterRfids(filterValues)
            const filteredRfidsResult = listRfids.filter(filterFn)
            setFilteredRfids(filteredRfidsResult)
        }
    }, [listRfids, filterValues])

    useEffect(() => {
        const { comparatorFn } = rfidsColumns[orderBy]
        if (comparatorFn) {
            const sortFn = rfidSort(sortOrder, comparatorFn)
            const sortedRfidsResult = [...filteredRfids].sort(sortFn)
            setSortedRfids(sortedRfidsResult)
        }
    }, [filteredRfids, sortOrder, orderBy])

    useEffect(() => {
        if (filteredRfids.length === 0 && filterValues) {
            setNoResultFilter(true)
        } else {
            setNoResultFilter(false)
        }
    }, [filteredRfids, filterValues])

    useEffect(() => {
        if (listRfids.length) {
            const countUnactive = listRfids.filter(
                (item) => item.status === RfidStatus.ToBeActivate
            ).length
            setNumberUnactive(countUnactive)
        }
    }, [listRfids])

    const { viewMode, setViewMode } = UseRfidContext()
    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1.5rem",
                    marginBottom: "5rem",
                    height: "auto",
                }}
            >
                {viewMode === "EDIT" ? (
                    <EditRuleRfid />
                ) : viewMode === "ACTIVATION" ? (
                    <RfidActivationForm
                        RfidCardSource={listRfids as ListRfidInfo}
                        goBackToList={() => {
                            setViewMode("LIST")
                            fetchListRfid()
                        }}
                    />
                ) : (
                    <>
                        <RfidFilter onSubmit={onFilterSubmit} />
                        <MainPageContentContainer sx={{ p: "2.25rem" }}>
                            <RfidContainer
                                apiSuccess={success}
                                rows={sortedRfids}
                                loading={isLoading}
                                sortOrder={sortOrder}
                                orderBy={orderBy}
                                setSortOrder={setSortOrder}
                                setOrderBy={setOrderBy}
                                noResultFilter={noResultFilter}
                            />
                        </MainPageContentContainer>

                        <Box
                            sx={{
                                alignItem: "center",
                                justifyContent: "left",
                                paddingLeft: "24px",
                                width: "100vw",
                                ...footerStyle,
                            }}
                        >
                            {numberUnactive !== 0 && (
                                <StatusWarning
                                    text={getUnactivateCardText(numberUnactive)}
                                />
                            )}
                        </Box>

                        <OreveFooter
                            sx={{
                                justifyContent: "right",
                                paddingRight: "24px",
                                width: "80vw",
                                gap: "1rem",
                            }}
                        >
                            {featureContext && (
                                <OreveButton
                                    sx={{ height: "40px" }}
                                    variant="outlined"
                                    value="Commander une carte"
                                    icon={ShoppingBagOutlined}
                                />
                            )}
                            <OreveButton
                                data-testid="download_sessions_csv"
                                data-cy="rf--downloadcsv"
                                sx={{ height: "40px" }}
                                loading={isLoading}
                                onClick={() => {
                                    setFilterObject({
                                        export: true,
                                        filter: {
                                            cardHolder:
                                                filterValues?.cardHolder?.trim() ??
                                                "",
                                            rfidNumber:
                                                filterValues?.rfidNumber?.trim() ??
                                                "",
                                            status:
                                                filterValues?.status ?? "all",
                                            type: selectedButton,
                                        },
                                    })
                                }}
                                variant="outlined"
                                value="Exporter en format .csv"
                                icon={SaveAltOutlined}
                            />
                            <OreveButton
                                data-cy="cards__footer--add-card"
                                sx={{ height: "40px" }}
                                value="Activer une carte"
                                onClick={() => setViewMode("ACTIVATION")}
                                icon={AddOutlined}
                                disabled={numberUnactive === 0}
                            />
                        </OreveFooter>
                    </>
                )}
            </Box>
        </>
    )
}

const RfidContainer = ({
    apiSuccess,
    rows,
    loading,
    sortOrder,
    orderBy,
    setSortOrder,
    setOrderBy,
    noResultFilter,
}: {
    apiSuccess: boolean | null
    rows: ListRfidInfo
    loading: boolean
    sortOrder: SortOrder
    orderBy: keyof RfidsTableColumnsType
    setSortOrder: React.Dispatch<React.SetStateAction<SortOrder>>
    setOrderBy: React.Dispatch<
        React.SetStateAction<keyof RfidsTableColumnsType>
    >
    noResultFilter: boolean
}) => {
    const {
        currentCardSelected,
        rfidCurrentStatus,
        getFilteredRows,
        selectedButton,
        setSelectedButton,
        displayTable,
        setDisplayTable,
    } = UseRfidContext()
    const { fetchListChargingRules } = useChargingRulesContext()

    useEffect(() => {
        fetchListChargingRules()
    }, [])

    return loading || !apiSuccess ? (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
            {!apiSuccess && !loading ? <ConnexionError /> : <Loader />}
        </Box>
    ) : (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: "1.313rem",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 2,
                    alignItems: { xs: "stretch", sm: "center" },
                    mb: "2.25rem",
                    flexWrap: "wrap",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        flex: { xs: "1 1 100%", sm: "0 1 auto" },
                    }}
                >
                    <Typography
                        variant="headlineLarge"
                        sx={{
                            color: "grey.900",
                        }}
                    >
                        Liste des cartes de recharge
                    </Typography>
                </Box>

                <Box
                    sx={{
                        flex: { xs: "1 1 100%", sm: "0 1 auto" },
                        display: "flex",
                        justifyContent: { xs: "flex-start", sm: "center" },
                    }}
                >
                    <SelectableButtonGroup
                        buttons={tableType}
                        selected={displayTable}
                        onChange={(id) => setDisplayTable(id)}
                        orientation={"horizontal"}
                    />
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        flex: { xs: "1 1 100%", sm: "1 1 auto" },
                        justifyContent: {
                            xs: "flex-start",
                            sm: "flex-start",
                            md: "flex-start",
                            lg: "flex-end",
                        },
                        minWidth: { xs: "100%", sm: "auto" },
                    }}
                >
                    <SelectableButtonGroup
                        buttons={buttonDefs}
                        selected={selectedButton}
                        onChange={(id) => setSelectedButton(id)}
                    />
                </Box>
            </Box>
            {noResultFilter ? (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Typography sx={{ color: "grey.600" }}>
                        Aucun résultat
                    </Typography>
                </Box>
            ) : (
                <RfidList
                    rows={getFilteredRows(rows)}
                    sortOrder={sortOrder}
                    orderBy={orderBy}
                    setSortOrder={setSortOrder}
                    setOrderBy={setOrderBy}
                />
            )}
            {rfidCurrentStatus() === 1 && currentCardSelected && (
                <RfidDisableCard key={currentCardSelected.id} />
            )}

            {rfidCurrentStatus() === 2 && currentCardSelected && (
                <RfidReactivateCard key={currentCardSelected.id} />
            )}
        </Box>
    )
}

const RfidList = ({
    rows,
    sortOrder,
    orderBy,
    setSortOrder,
    setOrderBy,
}: {
    rows: ListRfidInfo
    sortOrder: SortOrder
    orderBy: keyof RfidsTableColumnsType
    setSortOrder: React.Dispatch<React.SetStateAction<SortOrder>>
    setOrderBy: React.Dispatch<
        React.SetStateAction<keyof RfidsTableColumnsType>
    >
}) => {
    const { displayTable } = UseRfidContext()

    return displayTable == "table" ? (
        <RfidTable
            rows={rows}
            sortOrder={sortOrder}
            orderBy={orderBy}
            setSortOrder={setSortOrder}
            setOrderBy={setOrderBy}
        />
    ) : (
        <RfidCard rows={rows} />
    )
}
